import { Link } from 'react-router-dom';
import { MessageCircle, Phone, Clock, CheckCircle } from 'lucide-react';

interface FreeConsultationBannerProps {
  variant?: 'full' | 'compact' | 'inline';
  className?: string;
}

export function FreeConsultationBanner({ variant = 'full', className = '' }: FreeConsultationBannerProps) {
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 text-sm ${className}`}>
        <MessageCircle className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="text-muted-foreground">
          Not sure yet?{' '}
          <Link to="/contact?consultation=true" className="text-primary hover:underline font-medium">
            Book a free consultation
          </Link>
        </span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to="/contact?consultation=true"
        className={`flex items-center gap-3 p-4 bg-primary/5 border border-primary/20 hover:border-primary/40 transition-all group ${className}`}
      >
        <MessageCircle className="h-5 w-5 text-primary flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
            Free Consultation
          </p>
          <p className="text-xs text-muted-foreground">No pressure — let's chat about your vision</p>
        </div>
      </Link>
    );
  }

  return (
    <section className={`py-10 bg-primary/5 border-y border-primary/20 ${className}`} aria-label="Free consultation offer">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs uppercase tracking-widest font-medium px-4 py-1.5 mb-4">
            <MessageCircle className="h-3.5 w-3.5" />
            Complimentary
          </div>
          <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
            Start With a Free Consultation
          </h3>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            Not sure which session is right for you? Let's hop on a quick call — no pressure, no commitment. 
            We'll talk about your vision and find the perfect fit.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 mb-6 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Phone className="h-4 w-4 text-primary" /> Phone or video
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-primary" /> 100% free
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/contact?consultation=true"
              className="inline-flex items-center justify-center gap-2 bg-gradient-gold text-primary-foreground px-6 py-3 text-sm font-medium tracking-wider uppercase hover:shadow-gold transition-all"
            >
              Book Consultation Now
            </Link>
            <a
              href="tel:+14805766053"
              className="inline-flex items-center justify-center gap-2 border border-primary text-primary px-6 py-3 text-sm font-medium tracking-wider uppercase hover:bg-primary hover:text-primary-foreground transition-all"
            >
              <Phone className="h-4 w-4" /> Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
